"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useTranslations } from "next-intl";
import { track } from "@/lib/analytics";

function stripMarkdown(raw: string): string {
  return raw
    // Remove fenced code blocks
    .replace(/```[\s\S]*?```/g, "")
    // Remove inline code
    .replace(/`[^`]*`/g, "")
    // Remove MDX/HTML tags
    .replace(/<[^>]+>/g, "")
    // Replace links with their text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    // Remove images
    .replace(/!\[[^\]]*\]\([^)]+\)/g, "")
    // Remove heading markers
    .replace(/^#{1,6}\s+/gm, "")
    // Remove bold/italic
    .replace(/(\*{1,3}|_{1,3})(.*?)\1/g, "$2")
    // Remove horizontal rules
    .replace(/^[-*_]{3,}\s*$/gm, "")
    // Collapse multiple blank lines
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// useSyncExternalStore is the React-idiomatic way to read browser-only APIs
// without causing hydration mismatches (same pattern as ConsentBanner.tsx).
function subscribeNoop() {
  return () => {};
}

type State = "idle" | "playing" | "paused";

export default function AudioPlayer({
  content,
  title,
}: {
  content: string;
  title: string;
}) {
  const t = useTranslations("audioPlayer");
  const supported = useSyncExternalStore(
    subscribeNoop,
    () => typeof window !== "undefined" && "speechSynthesis" in window,
    () => false
  );

  const [state, setState] = useState<State>("idle");
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const playStartRef = useRef<number>(0);

  useEffect(() => {
    return () => {
      window.speechSynthesis?.cancel();
    };
  }, []);

  if (!supported) return null;

  function pickBestVoice(): SpeechSynthesisVoice | null {
    const all = window.speechSynthesis.getVoices();
    const preferred: Array<(v: SpeechSynthesisVoice) => boolean> = [
      (v) => /natural|premium/i.test(v.name) && v.lang.startsWith("en"),
      (v) => v.name === "Google US English",
      (v) => v.name === "Samantha",
      (v) => v.name === "Karen",
      (v) => v.name === "Daniel",
      (v) => v.name === "Google UK English Female",
      (v) => v.name === "Google UK English Male",
      (v) => v.lang.startsWith("en"),
    ];
    for (const predicate of preferred) {
      const match = all.find(predicate);
      if (match) return match;
    }
    return null;
  }

  function handlePlay() {
    if (state === "paused") {
      window.speechSynthesis.resume();
      setState("playing");
      return;
    }

    window.speechSynthesis.cancel();
    const text = `${title}. ${stripMarkdown(content)}`;
    const utterance = new SpeechSynthesisUtterance(text);
    const voice = pickBestVoice();
    if (voice) utterance.voice = voice;
    utterance.onend = () => {
      const elapsed = Math.round((Date.now() - playStartRef.current) / 1000);
      track("audio_complete", { post_title: title, elapsed_seconds: elapsed });
      setState("idle");
    };
    utterance.onerror = () => setState("idle");
    utteranceRef.current = utterance;
    playStartRef.current = Date.now();
    window.speechSynthesis.speak(utterance);
    setState("playing");
    track("audio_play", { post_title: title });
  }

  function handlePause() {
    window.speechSynthesis.pause();
    const elapsed = Math.round((Date.now() - playStartRef.current) / 1000);
    setState("paused");
    track("audio_pause", { post_title: title, elapsed_seconds: elapsed });
  }

  function handleStop() {
    window.speechSynthesis.cancel();
    const elapsed = Math.round((Date.now() - playStartRef.current) / 1000);
    utteranceRef.current = null;
    setState("idle");
    track("audio_complete", { post_title: title, elapsed_seconds: elapsed });
  }

  const isActive = state !== "idle";

  return (
    <div className="flex items-center justify-between gap-4 bg-[var(--surface)] border border-white/5 rounded-lg px-4 py-3 my-6">
      {/* Left: label */}
      <div className="flex items-center gap-2.5 min-w-0">
        <svg
          className="w-4 h-4 shrink-0 text-[var(--accent-text)]"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
          />
        </svg>
        <span className="text-sm text-[var(--muted)] truncate">
          {state === "playing"
            ? t("playing")
            : state === "paused"
            ? t("paused")
            : t("listen")}
        </span>
      </div>

      {/* Right: controls */}
      <div className="flex items-center gap-2 shrink-0">
        {/* Play / Pause */}
        <button
          onClick={state === "playing" ? handlePause : handlePlay}
          aria-label={state === "playing" ? t("pause") : t("play")}
          className={`flex items-center min-h-[44px] gap-1.5 text-sm px-3 py-1.5 rounded-md border transition-colors ${
            isActive
              ? "border-[var(--accent)]/40 text-[var(--accent-text)] hover:bg-[var(--accent)]/10"
              : "border-white/10 text-[var(--muted)] hover:text-[var(--text)] hover:border-white/20"
          }`}
        >
          {state === "playing" ? (
            <>
              <svg
                className="w-3.5 h-3.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
              {t("pause")}
            </>
          ) : (
            <>
              <svg
                className="w-3.5 h-3.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
              {state === "paused" ? t("resume") : t("play")}
            </>
          )}
        </button>

        {/* Stop — only when active */}
        {isActive && (
          <button
            onClick={handleStop}
            aria-label={t("stop")}
            className="flex items-center min-h-[44px] gap-1.5 text-sm px-3 py-1.5 rounded-md border border-white/10 text-[var(--muted)] hover:text-[var(--text)] hover:border-white/20 transition-colors"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M6 6h12v12H6z" />
            </svg>
            {t("stop")}
          </button>
        )}
      </div>
    </div>
  );
}
